import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DeleteSweep as BulkDeleteIcon,
  ViewModule as CardViewIcon,
  ViewList as ListViewIcon,
} from '@mui/icons-material';
import { adminUserService, PendingUser, UserStats } from '../../services/api';
import { Checkbox, FormControlLabel, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useSnackbar } from '../../contexts/SnackbarContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const UserManagement: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [allUsers, setAllUsers] = useState<PendingUser[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PendingUser | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<PendingUser>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'card' | 'grid'>('card');
  const [processingUserId, setProcessingUserId] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  const loadData = async () => {
    setLoading(true);
    try {
      const [pending, all, statistics] = await Promise.all([
        adminUserService.getPendingUsers(),
        adminUserService.getAllUsers(),
        adminUserService.getUserStats(),
      ]);
      
      setPendingUsers(pending);
      setAllUsers(all);
      setStats(statistics);
    } catch (error) {
      console.error('Error loading user data:', error);
      showSnackbar('Failed to load user data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleApprove = async (user: PendingUser) => {
    setProcessingUserId(user._id);
    showSnackbar(`Approving ${user.name}...`, 'info');
    
    try {
      await adminUserService.approveUser(user._id, user.role);
      showSnackbar(`${user.name} has been approved!`, 'success');
      loadData(); // Reload data
    } catch (error) {
      console.error('Error approving user:', error);
      showSnackbar('Failed to approve user', 'error');
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleRejectClick = (user: PendingUser) => {
    setSelectedUser(user);
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedUser) return;

    try {
      await adminUserService.rejectUser(selectedUser._id, rejectionReason);
      showSnackbar(`${selectedUser.name}'s registration has been rejected`, 'info');
      setRejectDialogOpen(false);
      setRejectionReason('');
      setSelectedUser(null);
      loadData(); // Reload data
    } catch (error) {
      console.error('Error rejecting user:', error);
      showSnackbar('Failed to reject user', 'error');
    }
  };

  const handleEditClick = (user: PendingUser) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      email: user.email,
      department: user.department,
      jobTitle: user.jobTitle,
      role: user.role,
      accountStatus: user.accountStatus,
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!selectedUser) return;

    try {
      await adminUserService.updateUser(selectedUser._id, editFormData);
      showSnackbar(`${editFormData.name || selectedUser.name} has been updated!`, 'success');
      setEditDialogOpen(false);
      setSelectedUser(null);
      setEditFormData({});
      loadData();
    } catch (error) {
      console.error('Error updating user:', error);
      showSnackbar('Failed to update user', 'error');
    }
  };

  const handleDeleteClick = (user: PendingUser) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    try {
      await adminUserService.deleteUser(selectedUser._id);
      showSnackbar(`${selectedUser.name} has been deleted`, 'success');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      showSnackbar(error.response?.data?.error || 'Failed to delete user', 'error');
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedUserIds.length === 0) {
      showSnackbar('Please select users to delete', 'warning');
      return;
    }
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      const result = await adminUserService.bulkDeleteUsers(selectedUserIds);
      showSnackbar(`Successfully deleted ${result.deletedCount} user(s)`, 'success');
      setBulkDeleteDialogOpen(false);
      setSelectedUserIds([]);
      loadData();
    } catch (error: any) {
      console.error('Error bulk deleting users:', error);
      showSnackbar(error.response?.data?.error || 'Failed to delete users', 'error');
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === allUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(allUsers.map(u => u._id));
    }
  };

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'card' | 'grid' | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'suspended':
        return 'default';
      default:
        return 'default';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'data-steward':
        return 'primary';
      case 'user':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderGridView = (users: PendingUser[], showCheckbox: boolean = false) => (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            {showCheckbox && (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedUserIds.length === users.length && users.length > 0}
                  indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < users.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
            )}
            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Job Title</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Registered</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow
              key={user._id}
              hover
              onClick={() => handleEditClick(user)}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: '#f5f9ff',
                },
              }}
            >
              {showCheckbox && (
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedUserIds.includes(user._id)}
                    onChange={() => handleSelectUser(user._id)}
                  />
                </TableCell>
              )}
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#003366' }}>
                  {user.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{user.email}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{user.department || '-'}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{user.jobTitle || '-'}</Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={user.role}
                  color={getRoleColor(user.role)}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={user.accountStatus}
                  color={getStatusColor(user.accountStatus)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                  {formatDate(user.createdAt)}
                </Typography>
              </TableCell>
              <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                  {user.accountStatus === 'pending' && (
                    <>
                      <Tooltip title={processingUserId === user._id ? "Approving..." : "Approve"}>
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            disabled={processingUserId === user._id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApprove(user);
                            }}
                          >
                            {processingUserId === user._id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <ApproveIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={processingUserId === user._id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRejectClick(user);
                          }}
                        >
                          <RejectIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(user);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(user);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderUserCard = (user: PendingUser, showActions: boolean = true, showCheckbox: boolean = false) => (
    <Card 
      key={user._id} 
      onClick={() => handleEditClick(user)}
      role="button"
      tabIndex={0}
      aria-label={`Edit ${user.name}`}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleEditClick(user);
        }
      }}
      sx={{ 
        mb: 2,
        border: '1px solid #e0e0e0',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
          borderColor: '#003366',
        },
        '&:focus': {
          outline: '2px solid #003366',
          outlineOffset: '2px',
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showCheckbox && (
              <Checkbox
                checked={selectedUserIds.includes(user._id)}
                onChange={() => handleSelectUser(user._id)}
                onClick={(e) => e.stopPropagation()}
                onKeyPress={(e) => e.stopPropagation()}
              />
            )}
            <Box>
              <Typography variant="h6" component="div" sx={{ color: '#003366', fontWeight: 600 }}>
                {user.name}
              </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Chip 
                label={user.accountStatus} 
                color={getStatusColor(user.accountStatus)} 
                size="small" 
              />
              <Chip 
                label={user.role} 
                color={getRoleColor(user.role)} 
                size="small" 
                variant="outlined"
              />
              </Box>
            </Box>
          </Box>
          {showActions && (
            <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Edit user">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(user);
                  }} 
                  color="primary"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete user">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(user);
                  }} 
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <EmailIcon sx={{ fontSize: 18, mr: 1, color: '#666' }} />
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
          </Grid>

          {user.department && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BusinessIcon sx={{ fontSize: 18, mr: 1, color: '#666' }} />
                <Typography variant="body2" color="text.secondary">
                  {user.department}
                </Typography>
              </Box>
            </Grid>
          )}

          {user.jobTitle && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WorkIcon sx={{ fontSize: 18, mr: 1, color: '#666' }} />
                <Typography variant="body2" color="text.secondary">
                  {user.jobTitle}
                </Typography>
              </Box>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CalendarIcon sx={{ fontSize: 18, mr: 1, color: '#666' }} />
              <Typography variant="body2" color="text.secondary">
                Registered: {formatDate(user.createdAt)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>

      {showActions && user.accountStatus === 'pending' && (
        <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }} onClick={(e) => e.stopPropagation()}>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={processingUserId === user._id}
            startIcon={<RejectIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleRejectClick(user);
            }}
          >
            Reject
          </Button>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={processingUserId === user._id}
            startIcon={processingUserId === user._id ? <CircularProgress size={16} /> : <ApproveIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleApprove(user);
            }}
          >
            {processingUserId === user._id ? 'Approving...' : 'Approve'}
          </Button>
        </CardActions>
      )}
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ color: '#003366', fontWeight: 700 }}
        >
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
            size="small"
          >
            <ToggleButton value="card" aria-label="card view">
              <Tooltip title="Card View">
                <CardViewIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <Tooltip title="Grid View">
                <ListViewIcon />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh data">
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5' }}>
              <Typography variant="h3" sx={{ color: '#003366', fontWeight: 700 }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Users
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#fff3e0' }}>
              <Typography variant="h3" sx={{ color: '#f57c00', fontWeight: 700 }}>
                {stats.byStatus.find(s => s._id === 'pending')?.count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Approval
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#e8f5e9' }}>
              <Typography variant="h3" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                {stats.byStatus.find(s => s._id === 'approved')?.count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Approved
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#ffebee' }}>
              <Typography variant="h3" sx={{ color: '#c62828', fontWeight: 700 }}>
                {stats.byStatus.find(s => s._id === 'rejected')?.count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rejected
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          aria-label="user management tabs"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
            },
          }}
        >
          <Tab 
            label={`Pending (${pendingUsers.length})`} 
            icon={<PersonIcon />}
            iconPosition="start"
          />
          <Tab 
            label={`All Users (${allUsers.length})`}
            icon={<PersonIcon />}
            iconPosition="start"
          />
        </Tabs>

        {/* Pending Users Tab */}
        <TabPanel value={tabValue} index={0}>
          {pendingUsers.length === 0 ? (
            <Alert severity="info">
              No pending user registrations at this time.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''} waiting for approval
              </Typography>
              {viewMode === 'grid' ? (
                renderGridView(pendingUsers, false)
              ) : (
                pendingUsers.map(user => renderUserCard(user, true))
              )}
            </Box>
          )}
        </TabPanel>

        {/* All Users Tab */}
        <TabPanel value={tabValue} index={1}>
          {allUsers.length === 0 ? (
            <Alert severity="info">
              No users found.
            </Alert>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {viewMode === 'card' && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedUserIds.length === allUsers.length && allUsers.length > 0}
                          indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < allUsers.length}
                          onChange={handleSelectAll}
                        />
                      }
                      label="Select All"
                    />
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Showing all {allUsers.length} user{allUsers.length !== 1 ? 's' : ''}
                    {selectedUserIds.length > 0 && ` (${selectedUserIds.length} selected)`}
                  </Typography>
                </Box>
                {selectedUserIds.length > 0 && (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<BulkDeleteIcon />}
                    onClick={handleBulkDeleteClick}
                    size="small"
                  >
                    Delete Selected ({selectedUserIds.length})
                  </Button>
                )}
              </Box>
              {viewMode === 'grid' ? (
                renderGridView(allUsers, true)
              ) : (
                allUsers.map(user => renderUserCard(user, true, true))
              )}
            </Box>
          )}
        </TabPanel>
      </Paper>

      {/* Rejection Dialog */}
      <Dialog 
        open={rejectDialogOpen} 
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject User Registration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to reject <strong>{selectedUser?.name}</strong>'s registration?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for rejection (optional)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="e.g., Invalid credentials, unauthorized access attempt"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleRejectConfirm} 
            color="error" 
            variant="contained"
          >
            Reject User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={editFormData.name || ''}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={editFormData.email || ''}
              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
            />
            <TextField
              fullWidth
              label="Department"
              value={editFormData.department || ''}
              onChange={(e) => setEditFormData({ ...editFormData, department: e.target.value })}
            />
            <TextField
              fullWidth
              label="Job Title"
              value={editFormData.jobTitle || ''}
              onChange={(e) => setEditFormData({ ...editFormData, jobTitle: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editFormData.role || 'user'}
                label="Role"
                onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value as any })}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="data-steward">Data Steward</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Account Status</InputLabel>
              <Select
                value={editFormData.accountStatus || 'pending'}
                label="Account Status"
                onChange={(e) => setEditFormData({ ...editFormData, accountStatus: e.target.value as any })}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to permanently delete <strong>{selectedUser?.name}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. The user will be permanently removed from the system.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Multiple Users</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to permanently delete <strong>{selectedUserIds.length}</strong> user(s)?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. All selected users will be permanently removed from the system.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkDeleteConfirm} variant="contained" color="error">
            Delete {selectedUserIds.length} User(s)
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagement;
